// src/components/ui/TagInput.tsx
// Generic multi-select tag input with configurable labels
import React, { useState, useRef, KeyboardEvent, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TagInputProps {
    value: string[];
    onChange: (tags: string[]) => void;
    suggestions?: string[];
    placeholder?: string;
    maxItems?: number;
    className?: string;
    helperText?: string;
    showHelperText?: boolean;
    // Configurable labels for internationalization
    suggestionsLabel?: string;
    createNewLabel?: string;
    createNewPrefix?: string;
}

const TagInput: React.FC<TagInputProps> = ({
                                               value = [],
                                               onChange,
                                               suggestions = [],
                                               placeholder = 'Type and press Enter to add...',
                                               maxItems,
                                               className,
                                               helperText = 'Press Enter to add',
                                               showHelperText = true,
                                               suggestionsLabel = 'Suggestions',
                                               createNewLabel = 'Create new',
                                               createNewPrefix = 'Add'
                                           }) => {
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const availableSuggestions = suggestions.filter(s => !value.includes(s));
    const filteredSuggestions = availableSuggestions.filter(s =>
        s.toLowerCase().includes(inputValue.toLowerCase())
    );

    // Check if input is a custom entry (not in suggestions)
    const isCustomEntry = inputValue.trim() && !filteredSuggestions.includes(inputValue.trim());

    // Build list of all selectable items (custom entry first if exists)
    const allItems = [
        ...(isCustomEntry ? [inputValue.trim()] : []),
        ...filteredSuggestions
    ];

    // Reset selected index when filtered suggestions change
    useEffect(() => {
        setSelectedIndex(-1);
    }, [filteredSuggestions.length, inputValue]);

    // Scroll selected item into view
    useEffect(() => {
        if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
            itemRefs.current[selectedIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            });
        }
    }, [selectedIndex]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                containerRef.current &&
                !containerRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const addTag = (tag: string) => {
        const trimmed = tag.trim();
        if (!trimmed || value.includes(trimmed) || (maxItems && value.length >= maxItems)) {
            setInputValue('');
            return;
        }
        onChange([...value, trimmed]);
        setInputValue('');
        setIsOpen(false);
        setSelectedIndex(-1);
        setTimeout(() => inputRef.current?.focus(), 0);
    };

    const removeTag = (tagToRemove: string) => {
        onChange(value.filter(t => t !== tagToRemove));
        inputRef.current?.focus();
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (isOpen && allItems.length > 0) {
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedIndex(prev => (prev + 1) % allItems.length);
                return;
            }
            if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSelectedIndex(prev => (prev - 1 + allItems.length) % allItems.length);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (selectedIndex >= 0 && selectedIndex < allItems.length) {
                    addTag(allItems[selectedIndex]);
                } else if (inputValue.trim()) {
                    addTag(inputValue);
                }
                return;
            }
        } else {
            if (e.key === 'Enter' && inputValue.trim()) {
                e.preventDefault();
                addTag(inputValue);
                return;
            }
        }

        if (e.key === 'Backspace' && !inputValue && value.length > 0) {
            e.preventDefault();
            onChange(value.slice(0, -1));
        }

        if (e.key === 'Escape') {
            setIsOpen(false);
            setSelectedIndex(-1);
        }
    };

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue);
        setIsOpen(newValue.length > 0 && (filteredSuggestions.length > 0 || newValue.trim().length > 0));
    };

    const handleInputFocus = () => {
        // Show dropdown on focus if there are suggestions available
        if (suggestions.length > 0) {
            setIsOpen(true);
        }
    };

    return (
        <div ref={containerRef} className={cn('w-full relative', className)}>
            {/* Selected Tags */}
            {value.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {value.map((tag) => (
                        <Badge key={tag} variant="secondary" className="pl-3 pr-1 py-1.5 text-sm">
                            <span>{tag}</span>
                            <button
                                type="button"
                                onClick={() => removeTag(tag)}
                                className="ml-2 rounded-full p-0.5 hover:bg-secondary-foreground/20"
                            >
                                <X className="h-3 w-3" />
                            </button>
                        </Badge>
                    ))}
                </div>
            )}

            {/* Input */}
            <div className="relative">
                <Input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onFocus={handleInputFocus}
                    placeholder={placeholder}
                    disabled={maxItems ? value.length >= maxItems : false}
                    className="pr-10"
                />
                {inputValue && (
                    <button
                        type="button"
                        onClick={() => addTag(inputValue)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                        <Plus className="h-4 w-4 text-gray-600" />
                    </button>
                )}

                {/* DROPDOWN - Custom entry first, then suggestions */}
                {isOpen && (isCustomEntry || filteredSuggestions.length > 0) && (
                    <div
                        ref={dropdownRef}
                        className="absolute left-0 right-0 mt-1 bg-white border-2 border-gray-200 rounded-xl z-50"
                        style={{
                            maxHeight: '250px',
                            overflowY: 'scroll',
                            overflowX: 'hidden'
                        }}
                    >
                        {/* Create New - AT TOP */}
                        {isCustomEntry && (
                            <div style={{ padding: '4px 0' }}>
                                <div style={{
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#6b7280'
                                }}>
                                    {createNewLabel}
                                </div>
                                <div
                                    ref={(el) => itemRefs.current[0] = el}
                                    onClick={() => addTag(inputValue)}
                                    style={{
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        color: '#1A4D4F',
                                        fontWeight: 500,
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: selectedIndex === 0 ? '#f3f4f6' : 'transparent',
                                        transition: 'background-color 0.15s'
                                    }}
                                    onMouseEnter={() => setSelectedIndex(0)}
                                    onMouseLeave={() => setSelectedIndex(-1)}
                                >
                                    <Plus size={16} style={{ marginRight: '8px' }} />
                                    {createNewPrefix} "{inputValue.trim()}"
                                </div>
                            </div>
                        )}

                        {/* Suggestions - BELOW */}
                        {filteredSuggestions.length > 0 && (
                            <div style={{
                                padding: '4px 0',
                                borderTop: isCustomEntry ? '2px solid #e5e7eb' : 'none'
                            }}>
                                <div style={{
                                    padding: '6px 12px',
                                    fontSize: '12px',
                                    fontWeight: 600,
                                    color: '#6b7280'
                                }}>
                                    {suggestionsLabel}
                                </div>
                                {filteredSuggestions.map((suggestion, index) => {
                                    const itemIndex = isCustomEntry ? index + 1 : index;
                                    return (
                                        <div
                                            key={suggestion}
                                            ref={(el) => itemRefs.current[itemIndex] = el}
                                            onClick={() => addTag(suggestion)}
                                            style={{
                                                padding: '8px 12px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                backgroundColor: selectedIndex === itemIndex ? '#f3f4f6' : 'transparent',
                                                transition: 'background-color 0.15s'
                                            }}
                                            onMouseEnter={() => setSelectedIndex(itemIndex)}
                                            onMouseLeave={() => setSelectedIndex(-1)}
                                        >
                                            {suggestion}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Helper Text */}
            {showHelperText && (
                <p className="text-xs text-gray-500 mt-2">
                    {maxItems && <span className="mr-3">{value.length}/{maxItems} items</span>}
                    <span>{helperText}</span>
                </p>
            )}
        </div>
    );
};

export default TagInput;